import { ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSupabase } from '../../context/SupabaseContext';
import { LoadingState } from '../common/LoadingState';

interface ProtectedRouteProps {
  children: ReactNode;
}

interface AdminProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

interface SalonRoleProtectedRouteProps {
  children: ReactNode;
  allowedRoles: string[];
  redirectTo?: string;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { salonId, isLoading, error } = useSupabase();
  const location = useLocation();

  // Only show full-screen error UI on the very first load.
  const isColdStartRef = useRef(true);

  useEffect(() => {
    if (!isLoading) {
      isColdStartRef.current = false;
    }
  }, [isLoading]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-[var(--app-canvas)]">
        <LoadingState spinnerSize="xl" className="py-0" />
      </div>
    );
  }

  // Show error state ONLY on cold start (avoid taking over the app mid-session).
  if (isColdStartRef.current && error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-[var(--app-canvas)] px-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="mt-6 text-sm text-gray-500">
            <ul className="mt-2 space-y-1">
              <li>• {error}</li>
              <li>• Try refreshing the page</li>
              <li>• Contact support if issues continue</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (salonId === null) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Only redirect to /register if authenticated but not registered
  if (salonId === '') {
    return <Navigate to="/register" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function SalonRoleProtectedRoute({
  children,
  allowedRoles,
  redirectTo = '/',
}: SalonRoleProtectedRouteProps) {
  const { supabase, salonId, isLoading } = useSupabase();
  const [role, setRole] = useState<string | null>(null);
  const [loadingRole, setLoadingRole] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const loadRole = async () => {
      try {
        setLoadingRole(true);
        const { data: auth } = await supabase.auth.getUser();
        const userId = auth?.user?.id;
        if (!userId || !salonId) {
          setRole(null);
          return;
        }
        const { data } = await supabase
          .from('salon_users')
          .select('role')
          .eq('user_id', userId)
          .eq('salon_id', salonId)
          .maybeSingle();
        setRole(data?.role ?? null);
      } finally {
        setLoadingRole(false);
      }
    };

    loadRole();
  }, [supabase, salonId, location.pathname]);

  if (isLoading || loadingRole) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-50">
        <LoadingState spinnerSize="xl" className="py-0" />
      </div>
    );
  }

  if (!role || !allowedRoles.includes(role)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

export function AdminProtectedRoute({ children, redirectTo = '/' }: AdminProtectedRouteProps) {
  return (
    <SalonRoleProtectedRoute allowedRoles={['admin']} redirectTo={redirectTo}>
      {children}
    </SalonRoleProtectedRoute>
  );
}

export function AdminLikeProtectedRoute({ children, redirectTo = '/' }: AdminProtectedRouteProps) {
  return (
    <SalonRoleProtectedRoute allowedRoles={['admin', 'owner']} redirectTo={redirectTo}>
      {children}
    </SalonRoleProtectedRoute>
  );
}